import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import { RefreshCw } from 'lucide-react';

export default function AdminManagers() {
  const [managers, setManagers] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: mgrs } = await supabase
        .from('managers').select('*').order('tier');

      const { data: assigns } = await supabase
        .from('company_manager_assignments')
        .select('manager_id, companies(name, verification_status)')
        .eq('is_active', true);

      const { data: tickets } = await supabase
        .from('support_tickets').select('*')
        .order('created_at', { ascending: false });

      // Enrich managers
      const countMap = {};
      const companyMap = {};
      (assigns || []).forEach(a => {
        countMap[a.manager_id] = (countMap[a.manager_id] || 0) + 1;
        if (!companyMap[a.manager_id]) companyMap[a.manager_id] = [];
        if (a.companies) companyMap[a.manager_id].push(a.companies);
      });

      const ticketMap = {};
      (tickets || []).forEach(t => {
        if (!ticketMap[t.manager_id]) ticketMap[t.manager_id] = [];
        ticketMap[t.manager_id].push(t);
      });

      setManagers((mgrs || []).map(m => ({
        ...m,
        current_companies: countMap[m.id] || 0,
        companies: companyMap[m.id] || [],
        tickets: ticketMap[m.id] || [],
      })));

      setAllTickets(tickets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openTickets = allTickets.filter(t =>
    ['open', 'in_progress'].includes(t.status)).length;
  const resolvedTickets = allTickets.filter(t => t.status === 'resolved').length;

  return (
    <AdminLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>
          Manager Oversight
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>
          All managers, their workloads, and support ticket overview
        </p>
      </div>

      {/* Platform stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
        gap: 12, marginBottom: 28,
      }}>
        {[
          { l: 'Total Managers', v: managers.length, i: '👤', c: '#4F8EF7' },
          { l: 'Total Tickets', v: allTickets.length, i: '🎫', c: '#7C3AED' },
          { l: 'Open Tickets', v: openTickets, i: '🔔', c: '#F59E0B' },
          { l: 'Resolved', v: resolvedTickets, i: '✅', c: '#10B981' },
        ].map((s, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 14, padding: '16px 18px',
            border: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 24 }}>{s.i}</span>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>{s.l}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Manager cards */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2,1fr)',
        gap: 16, marginBottom: 28,
      }}>
        {managers.map(m => {
          const utilization = Math.round((m.current_companies / m.max_companies) * 100);
          const openT = m.tickets.filter(t => ['open','in_progress'].includes(t.status)).length;
          const urgentT = m.tickets.filter(t => t.priority === 'urgent' && t.status !== 'resolved').length;
          const isEnterprise = m.tier === 'enterprise';

          return (
            <div key={m.id} style={{
              background: '#fff', borderRadius: 16, padding: 20,
              border: isEnterprise
                ? '1.5px solid rgba(245,158,11,0.3)'
                : '1px solid #E2E8F0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <div style={{
                  width: 50, height: 50, borderRadius: '50%',
                  background: m.avatar_color || '#4F8EF7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 900, fontSize: 20,
                }}>
                  {m.name[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#1E293B' }}>
                      {m.name}
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 800, padding: '2px 7px',
                      borderRadius: 4,
                      background: isEnterprise ? 'rgba(245,158,11,0.1)' : 'rgba(79,142,247,0.1)',
                      color: isEnterprise ? '#F59E0B' : '#4F8EF7',
                    }}>
                      {isEnterprise ? '👑 ENTERPRISE' : '✨ STANDARD'}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                    {m.email}
                  </div>
                </div>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: m.is_active ? '#10B981' : '#EF4444',
                }} />
              </div>

              {/* Stats row */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
                gap: 8, marginBottom: 14,
              }}>
                {[
                  { l: 'Companies', v: `${m.current_companies}/${m.max_companies}`, c: '#4F8EF7' },
                  { l: 'Open Tickets', v: openT, c: '#F59E0B' },
                  { l: 'Urgent', v: urgentT, c: '#EF4444' },
                ].map((s, i) => (
                  <div key={i} style={{
                    background: '#F8FAFF', borderRadius: 8, padding: '8px 10px', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: s.c }}>{s.v}</div>
                    <div style={{ fontSize: 9, color: '#94A3B8' }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Utilization bar */}
              <div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 10, color: '#94A3B8', marginBottom: 4,
                }}>
                  <span>Capacity</span>
                  <span style={{
                    color: utilization > 80 ? '#EF4444' : utilization > 60 ? '#F59E0B' : '#10B981',
                    fontWeight: 700,
                  }}>
                    {utilization}%
                  </span>
                </div>
                <div style={{ height: 6, background: '#F1F5F9', borderRadius: 3 }}>
                  <div style={{
                    height: '100%', borderRadius: 3,
                    background: utilization > 80 ? '#EF4444'
                      : utilization > 60 ? '#F59E0B'
                      : m.avatar_color || '#4F8EF7',
                    width: `${utilization}%`,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* All tickets table */}
      <div style={{
        background: '#fff', borderRadius: 14,
        border: '1px solid #E2E8F0', overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
            All Support Tickets
          </div>
          <button onClick={load} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 8,
            border: '1px solid #E2E8F0', background: '#fff',
            fontSize: 12, cursor: 'pointer', color: '#64748B',
          }}>
            <RefreshCw size={12} />Refresh
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFF' }}>
                {['Ticket', 'Company', 'Manager', 'Priority', 'Status', 'Date'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 10, fontWeight: 700, color: '#94A3B8',
                    letterSpacing: 0.5, borderBottom: '1px solid #F1F5F9',
                  }}>{h.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allTickets.slice(0, 20).map(t => {
                const mgr = managers.find(m =>
                  m.tickets.some(mt => mt.id === t.id));
                const prioColors = {
                  low: '#94A3B8', medium: '#F59E0B',
                  high: '#EF4444', urgent: '#DC2626',
                };
                const statusColors = {
                  open: '#4F8EF7', in_progress: '#7C3AED',
                  waiting_company: '#F59E0B', resolved: '#10B981', closed: '#94A3B8',
                };
                return (
                  <tr key={t.id}
                    style={{ borderBottom: '1px solid #F1F5F9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFF'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>
                        {t.title}
                      </div>
                      <div style={{ fontSize: 10, color: '#94A3B8' }}>{t.category}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {t.submitted_by_name}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {mgr?.name || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4,
                        background: `${prioColors[t.priority] || '#94A3B8'}15`,
                        color: prioColors[t.priority] || '#94A3B8',
                        textTransform: 'uppercase',
                      }}>
                        {t.priority}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4,
                        background: `${statusColors[t.status] || '#94A3B8'}15`,
                        color: statusColors[t.status] || '#94A3B8',
                      }}>
                        {t.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color: '#64748B' }}>
                        {new Date(t.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric',
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}